import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/organisms/Navbar';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SearchableMultiSelect from '../components/SearchableMultiSelect';

const CreateResumePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [universities, setUniversities] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(false);
  
  // Predefined options
  const degreeOptions = [
    'Associate Degree',
    'Bachelor of Arts (BA)',
    'Bachelor of Science (BS)',
    'Bachelor of Engineering (BE)',
    'Bachelor of Technology (BTech)',
    'Bachelor of Business Administration (BBA)',
    'Master of Arts (MA)',
    'Master of Science (MS)',
    'Master of Engineering (ME)',
    'Master of Technology (MTech)',
    'Master of Business Administration (MBA)',
    'Doctor of Philosophy (PhD)',
    'Doctor of Medicine (MD)',
    'Juris Doctor (JD)',
    'High School Diploma',
    'Other'
  ];

  const fieldOptions = [
    'Computer Science',
    'Information Technology',
    'Software Engineering',
    'Data Science',
    'Artificial Intelligence',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Civil Engineering',
    'Business Administration',
    'Marketing',
    'Finance',
    'Accounting',
    'Economics',
    'Psychology',
    'Biology',
    'Chemistry',
    'Physics',
    'Mathematics',
    'English',
    'Communications',
    'Other'
  ];

  const skillsOptions = {
    technical: [
      'Web Development', 'Mobile Development', 'Cloud Computing', 'DevOps',
      'Database Management', 'API Development', 'Microservices', 'System Design',
      'Network Security', 'Cybersecurity', 'Machine Learning', 'Data Analysis',
      'UI/UX Design', 'Quality Assurance', 'Agile/Scrum', 'CI/CD'
    ],
    programming: [
      'JavaScript', 'Python', 'Java', 'C++', 'C#', 'TypeScript', 'Go', 'Rust',
      'PHP', 'Ruby', 'Swift', 'Kotlin', 'R', 'MATLAB', 'SQL', 'HTML/CSS'
    ],
    tools: [
      'Git', 'Docker', 'Kubernetes', 'Jenkins', 'AWS', 'Azure', 'GCP',
      'React', 'Angular', 'Vue.js', 'Node.js', 'Django', 'Flask', 'Spring Boot',
      'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Elasticsearch', 'Jira', 'Figma'
    ],
    soft_skills: [
      'Leadership', 'Communication', 'Teamwork', 'Problem Solving',
      'Critical Thinking', 'Time Management', 'Adaptability', 'Creativity',
      'Attention to Detail', 'Project Management', 'Presentation Skills',
      'Conflict Resolution', 'Negotiation', 'Mentoring', 'Strategic Planning'
    ]
  };

  // Fetch universities on mount
  useEffect(() => {
    const fetchUniversities = async () => {
      setLoadingUniversities(true);
      try {
        const response = await fetch('http://universities.hipolabs.com/search?country=United+States');
        const data = await response.json();
        const universityNames = data.map(uni => uni.name).sort();
        setUniversities(universityNames);
      } catch (error) {
        console.error('Error fetching universities:', error);
        // Fallback to Indian universities, especially Tamil Nadu-based
        setUniversities([
          // Tamil Nadu Universities
          'Anna University, Chennai',
          'Sri Sairam Engineering College',
          'Sai University',
          'IIT Madras - Indian Institute of Technology Madras',
          'NIT Trichy - National Institute of Technology Tiruchirappalli',
          'Madras Institute of Technology (MIT), Chennai',
          'PSG College of Technology, Coimbatore',
          'Coimbatore Institute of Technology',
          'Thiagarajar College of Engineering, Madurai',
          'SSN College of Engineering, Chennai',
          'SRM Institute of Science and Technology, Chennai',
          'VIT Vellore - Vellore Institute of Technology',
          'Amrita Vishwa Vidyapeetham, Coimbatore',
          'Madras University - University of Madras',
          'Bharathiar University, Coimbatore',
          'Periyar University, Salem',
          'Karunya Institute of Technology and Sciences, Coimbatore',
          'Sathyabama Institute of Science and Technology, Chennai',
          'Hindustan Institute of Technology and Science, Chennai',
          'Saveetha Engineering College, Chennai',
          'St. Joseph\'s College of Engineering, Chennai',
          'Velammal Engineering College, Chennai',
          // Other Major Indian Universities
          'IIT Bombay - Indian Institute of Technology Bombay',
          'IIT Delhi - Indian Institute of Technology Delhi',
          'IIT Kanpur - Indian Institute of Technology Kanpur',
          'IIT Kharagpur - Indian Institute of Technology Kharagpur',
          'IIT Roorkee - Indian Institute of Technology Roorkee',
          'BITS Pilani - Birla Institute of Technology and Science',
          'NIT Warangal - National Institute of Technology Warangal',
          'Delhi University - University of Delhi',
          'Jawaharlal Nehru University (JNU), Delhi',
          'Bangalore University',
          'Manipal Institute of Technology',
          'Amity University',
          'Lovely Professional University (LPU)',
          'Symbiosis International University, Pune',
          'Christ University, Bangalore',
          'Other'
        ]);
      } finally {
        setLoadingUniversities(false);
      }
    };
    
    fetchUniversities();
  }, []);
  
  const [formData, setFormData] = useState({
    // Personal Info
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    
    // Professional Summary
    summary: '',
    
    // Education
    education: [{
      institution: '',
      institutionOther: '',
      degree: '',
      degreeOther: '',
      field: '',
      fieldOther: '',
      gpa: '',
      startDate: '',
      endDate: '',
      location: '',
      relevant_coursework: []
    }],
    
    // Work Experience
    workExperience: [{
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      current: false,
      location: '',
      description: ''
    }],
    
    // Projects
    projects: [{
      title: '',
      description: '',
      technologies: '',
      link: '',
      startDate: '',
      endDate: '',
      achievements: []
    }],
    
    // Skills
    skills: {
      technical: [],
      programming: [],
      tools: [],
      soft_skills: []
    },
    
    // Courses/Certifications
    certifications: [{
      name: '',
      issuer: '',
      year: '',
      credential_id: ''
    }]
  });

  const steps = [
    { id: 1, title: 'Personal Info', icon: 'person' },
    { id: 2, title: 'Summary', icon: 'description' },
    { id: 3, title: 'Education', icon: 'school' },
    { id: 4, title: 'Work Experience', icon: 'work' },
    { id: 5, title: 'Projects', icon: 'code' },
    { id: 6, title: 'Skills', icon: 'star' },
    { id: 7, title: 'Certifications', icon: 'verified' },
    { id: 8, title: 'Review', icon: 'visibility' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayFieldChange = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, template) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], template]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async (enhanceWithAI = false) => {
    // Convert form data to resume format
    const resumeData = {
      personal_info: {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        linkedin: formData.linkedin
      },
      summary: formData.summary,
      experience: formData.workExperience.map(exp => ({
        title: exp.position,
        company: exp.company,
        duration: `${exp.startDate} - ${exp.current ? 'Present' : exp.endDate}`,
        location: exp.location,
        description: exp.description.split('\n').filter(d => d.trim())
      })),
      projects: formData.projects.map(proj => ({
        title: proj.title,
        description: proj.description,
        technologies: proj.technologies.split(',').map(t => t.trim()),
        duration: `${proj.startDate} - ${proj.endDate}`,
        achievements: proj.achievements
      })),
      skills: formData.skills,
      education: formData.education.map(edu => ({
        degree: edu.degree === 'Other' ? edu.degreeOther : edu.degree,
        institution: edu.institution === 'Other' ? edu.institutionOther : edu.institution,
        year: edu.endDate,
        gpa: edu.gpa,
        field: edu.field === 'Other' ? edu.fieldOther : edu.field,
        relevant_coursework: edu.relevant_coursework
      })),
      certifications: formData.certifications.filter(cert => cert.name)
    };

    // Navigate back to enhance page with data
    navigate('/enhance-resume', { 
      state: { 
        resumeData, 
        enhanceWithAI,
        fromCreate: true 
      } 
    });
  };

  const progress = (currentStep / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/enhance-resume')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <Icon name="arrow_back" size={20} />
            <span className="font-medium">Back to Upload</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Resume</h1>
          <p className="text-gray-600">Step {currentStep} of {steps.length}</p>
          
          {/* Progress Bar */}
          <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="person" className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                  <p className="text-gray-600">Let's start with your basic details</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="New York, NY"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="https://linkedin.com/in/johndoe"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Professional Summary */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Icon name="description" className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Professional Summary</h2>
                  <p className="text-gray-600">Write a brief overview of your professional background</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleInputChange('summary', e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Write a brief professional summary highlighting your key skills, experience, and career objectives... (2-3 sentences)"
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  💡 Tip: Keep it concise and focus on your unique value proposition
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Education */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Icon name="school" className="text-green-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Education</h2>
                  <p className="text-gray-600">Add your educational background</p>
                </div>
              </div>
              
              {formData.education.map((edu, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-xl space-y-4 relative">
                  {formData.education.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('education', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Institution <span className="text-red-500">*</span>
                      </label>
                      <SearchableMultiSelect
                        options={loadingUniversities ? ['Loading...'] : universities}
                        selectedValues={edu.institution ? [edu.institution] : []}
                        onChange={(values) => handleArrayFieldChange('education', index, 'institution', values[0] || '')}
                        placeholder={loadingUniversities ? "Loading universities..." : "Search or type university name"}
                        allowCustom={true}
                        multiSelect={false}
                        disabled={loadingUniversities}
                      />
                      {edu.institution === 'Other' && (
                        <input
                          type="text"
                          value={edu.institutionOther || ''}
                          onChange={(e) => handleArrayFieldChange('education', index, 'institutionOther', e.target.value)}
                          placeholder="Enter institution name"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                          required
                        />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Degree <span className="text-red-500">*</span>
                      </label>
                      <SearchableMultiSelect
                        options={degreeOptions}
                        selectedValues={edu.degree ? [edu.degree] : []}
                        onChange={(values) => handleArrayFieldChange('education', index, 'degree', values[0] || '')}
                        placeholder="Select or type degree"
                        allowCustom={true}
                        multiSelect={false}
                      />
                      {edu.degree === 'Other' && (
                        <input
                          type="text"
                          value={edu.degreeOther || ''}
                          onChange={(e) => handleArrayFieldChange('education', index, 'degreeOther', e.target.value)}
                          placeholder="Enter degree name"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                          required
                        />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Field of Study
                      </label>
                      <SearchableMultiSelect
                        options={fieldOptions}
                        selectedValues={edu.field ? [edu.field] : []}
                        onChange={(values) => handleArrayFieldChange('education', index, 'field', values[0] || '')}
                        placeholder="Select or type field"
                        allowCustom={true}
                        multiSelect={false}
                      />
                      {edu.field === 'Other' && (
                        <input
                          type="text"
                          value={edu.fieldOther || ''}
                          onChange={(e) => handleArrayFieldChange('education', index, 'fieldOther', e.target.value)}
                          placeholder="Enter field of study"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                          required
                        />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        GPA
                      </label>
                      <input
                        type="text"
                        value={edu.gpa}
                        onChange={(e) => handleArrayFieldChange('education', index, 'gpa', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="3.8/4.0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="month"
                        value={edu.startDate}
                        onChange={(e) => handleArrayFieldChange('education', index, 'startDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="month"
                        value={edu.endDate}
                        onChange={(e) => handleArrayFieldChange('education', index, 'endDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addArrayItem('education', {
                  institution: '',
                  institutionOther: '',
                  degree: '',
                  degreeOther: '',
                  field: '',
                  fieldOther: '',
                  gpa: '',
                  startDate: '',
                  endDate: '',
                  location: '',
                  relevant_coursework: []
                })}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Icon name="add" size={20} />
                Add Another Education
              </button>
            </div>
          )}

          {/* Step 4: Work Experience */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Icon name="work" className="text-orange-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Work Experience</h2>
                  <p className="text-gray-600">Add your professional work history</p>
                </div>
              </div>
              
              {formData.workExperience.map((exp, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-xl space-y-4 relative">
                  {formData.workExperience.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('workExperience', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={exp.position}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'position', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Software Engineer"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'company', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Company Name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="month"
                        value={exp.startDate}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'startDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="month"
                        value={exp.endDate}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'endDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={exp.current}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={exp.current}
                          onChange={(e) => handleArrayFieldChange('workExperience', index, 'current', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">I currently work here</span>
                      </label>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={exp.location}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'location', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="San Francisco, CA"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (one responsibility per line)
                      </label>
                      <textarea
                        value={exp.description}
                        onChange={(e) => handleArrayFieldChange('workExperience', index, 'description', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="• Developed and maintained web applications&#10;• Collaborated with cross-functional teams&#10;• Improved system performance by 40%"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addArrayItem('workExperience', {
                  company: '',
                  position: '',
                  startDate: '',
                  endDate: '',
                  current: false,
                  location: '',
                  description: ''
                })}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Icon name="add" size={20} />
                Add Another Experience
              </button>
            </div>
          )}

          {/* Step 5: Projects */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Icon name="code" className="text-indigo-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
                  <p className="text-gray-600">Showcase your key projects (Optional)</p>
                </div>
              </div>
              
              {formData.projects.map((proj, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-xl space-y-4 relative">
                  {formData.projects.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('projects', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Title
                      </label>
                      <input
                        type="text"
                        value={proj.title}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'title', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="E-commerce Platform"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={proj.description}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Brief description of the project..."
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Technologies (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={proj.technologies}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'technologies', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="React, Node.js, MongoDB, AWS"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="month"
                        value={proj.startDate}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'startDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="month"
                        value={proj.endDate}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'endDate', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Project Link
                      </label>
                      <input
                        type="url"
                        value={proj.link}
                        onChange={(e) => handleArrayFieldChange('projects', index, 'link', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://github.com/username/project"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addArrayItem('projects', {
                  title: '',
                  description: '',
                  technologies: '',
                  link: '',
                  startDate: '',
                  endDate: '',
                  achievements: []
                })}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Icon name="add" size={20} />
                Add Another Project
              </button>
            </div>
          )}

          {/* Step 6: Skills */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Icon name="star" className="text-yellow-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Skills</h2>
                  <p className="text-gray-600">Add your technical and soft skills</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {Object.entries(skillsOptions).map(([skillType, options]) => (
                  <div key={skillType} className="p-6 bg-gray-50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-700 mb-3 capitalize">
                      {skillType.replace('_', ' ')} <span className="text-gray-500 text-xs font-normal">(Click to see {options.length} options)</span>
                    </label>
                    
                    <SearchableMultiSelect
                      options={options}
                      selectedValues={formData.skills[skillType]}
                      onChange={(values) => {
                        setFormData(prev => ({
                          ...prev,
                          skills: {
                            ...prev.skills,
                            [skillType]: values
                          }
                        }));
                      }}
                      placeholder={`Click to see options or type to search ${skillType.replace('_', ' ')}`}
                      allowCustom={true}
                      multiSelect={true}
                    />
                    
                    {formData.skills[skillType].length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {formData.skills[skillType].map((skill, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2"
                          >
                            {skill}
                            <button
                              onClick={() => {
                                const newSkills = formData.skills[skillType].filter((_, i) => i !== idx);
                                setFormData(prev => ({
                                  ...prev,
                                  skills: {
                                    ...prev.skills,
                                    [skillType]: newSkills
                                  }
                                }));
                              }}
                              className="hover:text-red-600 transition-colors"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Select from the dropdown or type your own skills. You can add multiple skills in each category!
                </p>
              </div>
            </div>
          )}

          {/* Step 7: Certifications */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <Icon name="verified" className="text-teal-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Certifications</h2>
                  <p className="text-gray-600">Add your professional certifications (Optional)</p>
                </div>
              </div>
              
              {formData.certifications.map((cert, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-xl space-y-4 relative">
                  {formData.certifications.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('certifications', index)}
                      className="absolute top-4 right-4 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Certification Name
                      </label>
                      <input
                        type="text"
                        value={cert.name}
                        onChange={(e) => handleArrayFieldChange('certifications', index, 'name', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="AWS Certified Solutions Architect"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Issuing Organization
                      </label>
                      <input
                        type="text"
                        value={cert.issuer}
                        onChange={(e) => handleArrayFieldChange('certifications', index, 'issuer', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Amazon Web Services"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Year
                      </label>
                      <input
                        type="text"
                        value={cert.year}
                        onChange={(e) => handleArrayFieldChange('certifications', index, 'year', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="2024"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Credential ID (Optional)
                      </label>
                      <input
                        type="text"
                        value={cert.credential_id}
                        onChange={(e) => handleArrayFieldChange('certifications', index, 'credential_id', e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="ABC123XYZ"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addArrayItem('certifications', {
                  name: '',
                  issuer: '',
                  year: '',
                  credential_id: ''
                })}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
              >
                <Icon name="add" size={20} />
                Add Another Certification
              </button>
            </div>
          )}

          {/* Step 8: Review */}
          {currentStep === 8 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Icon name="visibility" className="text-purple-600" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Review Your Resume</h2>
                  <p className="text-gray-600">Check your information before proceeding</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="person" size={20} className="text-blue-600" />
                    Personal Information
                  </h3>
                  <p className="text-gray-700 font-medium">{formData.fullName || 'Not provided'}</p>
                  <p className="text-gray-600 text-sm">{formData.email || 'Not provided'}</p>
                  <p className="text-gray-600 text-sm">{formData.phone || 'Not provided'}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="description" size={20} className="text-purple-600" />
                    Professional Summary
                  </h3>
                  <p className="text-gray-700">{formData.summary || 'No summary added'}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="school" size={20} className="text-green-600" />
                    Education
                  </h3>
                  <p className="text-gray-700">
                    {formData.education.filter(e => e.institution).length} {formData.education.filter(e => e.institution).length === 1 ? 'degree' : 'degrees'} added
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="work" size={20} className="text-orange-600" />
                    Work Experience
                  </h3>
                  <p className="text-gray-700">
                    {formData.workExperience.filter(e => e.company).length} {formData.workExperience.filter(e => e.company).length === 1 ? 'position' : 'positions'} added
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="code" size={20} className="text-indigo-600" />
                    Projects
                  </h3>
                  <p className="text-gray-700">
                    {formData.projects.filter(p => p.title).length} {formData.projects.filter(p => p.title).length === 1 ? 'project' : 'projects'} added
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Icon name="star" size={20} className="text-yellow-600" />
                    Skills
                  </h3>
                  <p className="text-gray-700">
                    {Object.values(formData.skills).flat().length} skills added across all categories
                  </p>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border-2 border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-4 text-lg">What would you like to do next?</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => handleFinish(true)}
                    disabled={isProcessing}
                    className="p-6 bg-white rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-all text-left group"
                  >
                    <Icon name="auto_awesome" className="text-blue-600 text-3xl mb-3 group-hover:scale-110 transition-transform" />
                    <h4 className="font-bold text-gray-900 mb-2 text-lg">Enhance with AI</h4>
                    <p className="text-sm text-gray-600">Let AI optimize and improve your resume content for better results</p>
                  </button>
                  
                  <button
                    onClick={() => handleFinish(false)}
                    disabled={isProcessing}
                    className="p-6 bg-white rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-all text-left group"
                  >
                    <Icon name="check_circle" className="text-green-600 text-3xl mb-3 group-hover:scale-110 transition-transform" />
                    <h4 className="font-bold text-gray-900 mb-2 text-lg">Continue</h4>
                    <p className="text-sm text-gray-600">Skip AI enhancement and proceed directly to template selection</p>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1 || isProcessing}
            variant="outline"
            className="px-6 py-3"
          >
            <Icon name="arrow_back" className="mr-2" size={20} />
            Back
          </Button>
          
          {currentStep < 8 && (
            <Button
              onClick={handleNext}
              disabled={isProcessing}
              className="px-6 py-3"
            >
              Next
              <Icon name="arrow_forward" className="ml-2" size={20} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateResumePage;
